import { Colors, Lightning, Registry, Router } from "@lightningjs/sdk";
import { getMediaPosterProps, MENU_WIDTH, PAGES, showCardNames } from "../../lib/utils";
import { MediaType } from "../../lib/models";

// Explicit height for a categorized search row = group title (~50) + the card
// (poster + label space) + bottom padding. Computed the same way MediaList sizes
// cards (incl. the showCardNames label gating) so rows pack tightly with no gap.
const SEARCH_ROW_TYPE: Record<string, MediaType> = {
    movies: "Movie" as MediaType,
    series: "TvSeries" as MediaType,
    episodes: "Episode" as MediaType,
    channels: "ChannelContent" as MediaType
};
function searchRowHeight(groupId: string): number {
    const props = getMediaPosterProps(SEARCH_ROW_TYPE[groupId] ?? ("Movie" as MediaType));
    const labelOffset = showCardNames() ? (props.isChannel ? 56 : 106) : 0;
    const TITLE_AREA = 50;
    const BOTTOM_PAD = 12;
    return TITLE_AREA + props.h + labelOffset + BOTTOM_PAD;
}
import { Keyboard, InputField, List } from "@lightningjs/ui";
import { KeyboardKey } from "../../components/input/KeyboardKey/KeyboardKey";
import { MediaList } from "../../components/data-display/MediaList/MediaList";
import { search, searchUncategorized } from "../../api/queries";
import { LoadingCircle } from "../../components/feedback/LoadingCircle/LoadingCircle";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import theme from "../../lib/theme";
import { MediaModel } from "../../lib/models";
import configs from "../../lib/configs";

interface SearchTemplateSpec extends Lightning.Component.TemplateSpec {
    Page: {
        InputContainer: {
            Wrapper: {
                Input: typeof InputField;
            };
        };
        KeyboardContainer: {
            Keyboard: typeof Keyboard;
        };
        ListWrapper: {
            List: typeof List;
        };
        Loading: typeof LoadingCircle;
        NotFound: {
            Text: typeof TranslatableText;
        };
    };
}

interface SearchTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

const keyboardConfig = {
    layouts: {
        ABC: [
            ["A", "B", "C", "D", "E", "F"],
            ["G", "H", "I", "J", "K", "L"],
            ["M", "N", "O", "P", "Q", "R"],
            ["S", "T", "U", "V", "W", "X"],
            ["Y", "Z", "1", "2", "3", "4"],
            ["5", "6", "7", "8", "9", "0"],
            ["Clear", "Space", "Backspace"]
        ]
    },
    buttonTypes: {
        default: {
            type: KeyboardKey,
            label: {
                text: {
                    fontSize: 32
                }
            }
        },
        Clear: {
            type: KeyboardKey,
            w: theme.keyboard.w / 3,
            icon: "clear"
        },
        Space: {
            type: KeyboardKey,
            w: theme.keyboard.w / 3,
            icon: "space"
        },
        Backspace: {
            type: KeyboardKey,
            w: theme.keyboard.w / 3,
            icon: "backspace"
        }
    },
    styling: {
        align: "center", //aligns the rows when the width of the Keyboard component is bigger than 0 (zero).
        horizontalSpacing: 0, //spacing between the keys
        verticalSpacing: 0 //spacing between the rows
    }
};

export class Search
    extends Lightning.Component<SearchTemplateSpec, SearchTypeConfig>
    implements Lightning.Component.ImplementTemplateSpec<SearchTemplateSpec>
{
    _selectedMovie: undefined | MediaModel;
    _timeout: undefined | number;
    _abortController!: AbortController;
    _isSearchState = false;
    _isSearchLoading = false;

    _listIndex? = 0;

    static override _template(): Lightning.Component.Template<SearchTemplateSpec> {
        return {
            collision: true,
            Page: {
                collision: true,
                x: MENU_WIDTH,
                InputContainer: {
                    y: theme.banner.padding.y,
                    x: theme.layout.content.x,
                    w: theme.keyboard.w,
                    h: theme.keyboard.input.h,
                    clipping: true,
                    texture: Lightning.Tools.getRoundRect(
                        theme.keyboard.input.w,
                        theme.keyboard.input.h,
                        theme.keyboard.radius,
                        0,
                        undefined,
                        true,
                        Colors(theme.colors.bg.surface).get()
                    ),
                    Wrapper: {
                        x: 37,
                        y: 6,
                        w: (w) => w,
                        h: (h) => h,
                        clipping: true,
                        Input: {
                            y: 4,
                            w: theme.keyboard.input.w - 37 * 2,
                            type: InputField,
                            inputText: {
                                fontSize: theme.keyboard.input.font.size,
                                lineHeight: theme.keyboard.input.font.size + 4,
                                textColor: Colors(theme.colors.text.surface).get()
                            },
                            // @ts-expect-error types
                            cursor: {
                                h: theme.keyboard.input.h - 20,
                                w: 2,
                                color: Colors(theme.colors.text.surface).get()
                            },
                            maxLabelWidth: theme.keyboard.input.w - 37 * 2,
                            labelPositionStatic: false
                        }
                    }
                },
                KeyboardContainer: {
                    collision: true,
                    y: theme.keyboard.input.h + theme.banner.padding.y + 10,
                    x: theme.layout.content.x,
                    rect: true,
                    // color: Colors(theme.keyboard.key.text.color).get(),
                    w: theme.keyboard.w,
                    h: (theme.keyboard.w / 6) * 7 + 1,
                    texture: Lightning.Tools.getRoundRect(
                        theme.keyboard.input.w,
                        (theme.keyboard.w / 6) * 7 + 1,
                        theme.keyboard.radius,
                        0,
                        undefined,
                        true,
                        Colors(theme.colors.bg.surface).get()
                    ),
                    // shader: { type: Lightning.shaders.RoundedRectangle, radius: 6 },
                    // rect: true,
                    // w: theme.keyboard.w + 1,
                    // h: 680,
                    Keyboard: {
                        collision: true,
                        type: Keyboard,
                        config: keyboardConfig,
                        currentLayout: "ABC",
                        maxCharacters: 35,
                        signals: { onInputChanged: true, onSpace: true }
                    }
                },
                ListWrapper: {
                    collision: true,
                    clipping: true,
                    y: configs.search.categorized ? theme.banner.padding.y : theme.banner.padding.y - 22,
                    x: theme.layout.content.x + theme.keyboard.w + 32,
                    w: theme.layout.w - (theme.layout.content.x + theme.keyboard.w + 62 + MENU_WIDTH),
                    h: theme.layout.h - (configs.search.categorized ? 123 : theme.banner.padding.y),
                    List: {
                        type: List,
                        spacing: 42,
                        x: 22,
                        y: 22,
                        w: theme.layout.w - (theme.layout.content.x + theme.keyboard.w + 62 + MENU_WIDTH),
                        h: theme.layout.h - (configs.search.categorized ? 123 : theme.banner.padding.y),
                        direction: "column"
                    }
                },
                Loading: {
                    type: LoadingCircle,
                    alpha: 0,
                    y: 0,
                    x: theme.layout.content.x + theme.keyboard.w + 62,
                    w: theme.layout.w - (theme.layout.content.x + theme.keyboard.w + 62 + MENU_WIDTH),
                    h: theme.layout.h - 123
                },
                NotFound: {
                    alpha: 0,
                    y: 0,
                    x: theme.layout.content.x + theme.keyboard.w + 62,
                    w: theme.layout.w - (theme.layout.content.x + theme.keyboard.w + 62 + MENU_WIDTH),
                    h: theme.layout.h - 123,
                    Text: {
                        mount: 0.5,
                        x: (w) => w / 2,
                        y: (h) => h / 2,
                        type: TranslatableText,
                        key: "search.empty",
                        text: {
                            textAlign: "center",
                            textColor: Colors(theme.colors.text.surface).get()
                        }
                    }
                }
            }
        };
    }

    readonly Page = this.getByRef("Page")!;
    readonly List = this.Page.getByRef("ListWrapper")!.getByRef("List")!;
    readonly InputContainer = this.Page.getByRef("InputContainer")!;
    readonly InputContainerWrapper = this.InputContainer.getByRef("Wrapper")!;
    readonly Input = this.InputContainerWrapper.getByRef("Input")!;

    readonly KeyboardContainer = this.Page.getByRef("KeyboardContainer")!;
    readonly Keyboard = this.KeyboardContainer.getByRef("Keyboard")!;

    readonly Loading = this.Page.getByRef("Loading")!;
    readonly NotFound = this.Page.getByRef("NotFound")!;

    override _setup() {
        this._abortController = new AbortController();

        this.Keyboard.inputField(this.Input);

        this._setState("SearchState");
    }

    override historyState(params?: { index: number }) {
        if (params?.index) {
            this._listIndex = params.index;
        } else {
            return {
                index: this._listIndex
            };
        }
    }

    override _getFocused(): Lightning.Component | null | undefined {
        return this.Keyboard;
    }

    override _active() {
        this.application.emit("clearBackground");
        this.application.emit("setActivePage", PAGES.search.name);

        for (const item of this.Keyboard.children) {
            item.patch({
                collision: true
            });

            for (const subItem of item.children) {
                subItem.patch({
                    collision: true
                });
            }
        }
    }

    override _inactive() {
        this._cancelSearch();
        // this._finishSearch();

        // this.List.clear();

        this.stage.gc();
    }

    _cancelSearch() {
        if (this._isSearchLoading) {
            this._abortController.abort();

            this._abortController = new AbortController();
        }

        if (this._timeout) {
            Registry.clearTimeout(this._timeout);
            this._timeout = undefined;
        }
    }

    _finishSearch(data: undefined | MediaModel[] = undefined, reload = true) {
        this._isSearchState = false;

        if (data) {
            if (reload) {
                if (configs.search.categorized) {
                    this.List.reload(
                        data.map((item) => ({
                            type: MediaList,
                            mediaTypeId: item.id,
                            x: 0,
                            navigateOnSelect: true,
                            // Show the group name (Movies / Series / Episodes / Channels)
                            // above each search row.
                            categoryKey: (item as any).name,
                            showCategoryName: true,
                            // Explicit, gated height so rows pack tightly (no gap)
                            // and follow the card height when titles are hidden.
                            h: searchRowHeight(item.id),
                            items: item.items
                        }))
                    );
                } else {
                    this.List.reload([
                        {
                            type: MediaList,
                            // mediaTypeId: item.id,
                            x: 0,
                            navigateOnSelect: true,
                            h: this.List.h,
                            w: this.List.w,
                            showCategoryName: false,
                            isFullScreen: true,
                            items: data
                        }
                    ]);
                }
            } else {
                this.List.items.forEach((item, index) => {
                    const rowData = data.find((searchItem) => searchItem.id === item.mediaTypeId);
                    if (rowData) {
                        item.addItems(rowData.items);
                    }
                });
            }

            if (this._listIndex) {
                this.List.index = this._listIndex;
                this._listIndex = undefined;
            }
        }

        this._toggleLoader();
    }

    _toggleLoader() {
        if (this._isSearchState) {
            this._setState("SearchState");
        }

        this.Loading.patch({
            smooth: {
                alpha: this._isSearchState ? 1 : 0
            }
        });

        const listHasItems = this.List.items.length > 0;

        this.List.patch({
            smooth: {
                alpha: this._isSearchState || !listHasItems ? 0 : 1
            }
        });

        this.NotFound.patch({
            smooth: {
                alpha: this._isSearchState || listHasItems ? 0 : 1
            }
        });
    }

    onInputChanged({ input }: { input: string }) {
        this._cancelSearch();

        this._timeout = Registry.setTimeout(() => {
            this._timeout = undefined;
            this._isSearchState = true;
            this._toggleLoader();

            (async () => {
                this.List.clear();

                this._isSearchLoading = true;

                if (!input.length) {
                    this.NotFound.patch({
                        smooth: {
                            alpha: 0
                        }
                    });
                    this._finishSearch();
                } else {
                    try {
                        const data = configs.search.categorized
                            ? await search(
                                  {
                                      query: input,
                                      offset: 0,
                                      limit: 20
                                  },
                                  this._abortController.signal
                              )
                            : await searchUncategorized(
                                  {
                                      query: input,
                                      offset: 0,
                                      limit: 20
                                  },
                                  this._abortController.signal
                              );
                        this._finishSearch(data);
                    } catch (e: any) {
                        if (!e.aborted && e.name !== "AbortError") {
                            this._finishSearch();
                        }
                    }
                }

                this._isSearchLoading = false;
            })();
        }, 660);
    }

    async $categoryMoviesLoaded() {
        console.log(
            "this.List.items.map((item) => item.items.length",
            this.List.items.map((item) => item.items.length)
        );
        const data = await search(
            {
                query: this.Input.input,
                limit: 20,
                offset: Math.max(...this.List.items.map((item) => item.items.length)!)
            },
            this._abortController.signal
        );
        this._finishSearch(data, false);
    }

    $categorySelected() {
        this._setState("ResultsState");
    }

    $focusKey(key: string) {
        this.Keyboard.focus(key);
        this._setState("SearchState");

        window.dispatchEvent(new KeyboardEvent("keydown", { keyCode: 13, code: "13" }));
    }

    static override _states() {
        return [
            class SearchState extends this {
                override _handleRight() {
                    if (this.List.items.length > 0 && !this._isSearchLoading) {
                        this._setState("ResultsState");
                    }
                }
            },
            class ResultsState extends this {
                override _getFocused() {
                    return this.List;
                }

                override _handleLeft() {
                    this._setState("SearchState");
                }

                $movieSelected(data: MediaModel) {
                    this._listIndex = this.List.index;
                    this._selectedMovie = data;
                }
            }
        ];
    }

    override _handleBack() {
        if (!Router.getHistory().length) return true;

        Router.go(Router.getHistory().length * -1);

        return true;
    }
}
